/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { NumberField } from 'sarsaparilla';

type NumberFieldFilterProps = {
    label: string;
    units: string;
    id?: string;
    max: number;
    /** Filter group key, stored in `fg` as `<filter>:<value>` */
    filter: string;
    unitAfter?: string;
    /** Selected filter groups */
    fg: string[];
    onFilterGroupsChange: (fg: string[]) => void;
};

/**
 * Number field for a filter group that holds a value, e.g. group size. Zero
 * removes the filter. Changes go straight to the pending filters, which the
 * dialog already debounces before fetching.
 */
function NumberFieldFilterBase({
    label,
    units,
    id,
    max,
    filter,
    unitAfter,
    fg,
    onFilterGroupsChange,
}: NumberFieldFilterProps) {
    const onChange = (value: number) => {
        const others = fg.filter((item) => !item.includes(filter));
        onFilterGroupsChange(value === 0 ? others : [...others, `${filter}:${value}`]);
    };

    const current = fg.find((item) => item.includes(filter));
    const value = current ? Number(current.split(':')[1]) : 0;

    return (
        <div className="rec-search-filters-number-field-wrap">
            <NumberField
                visualLabel={label}
                units={units}
                min={0}
                max={max}
                id={id}
                value={value}
                onChange={onChange}
                shouldTriggerOnChange
                unitAfter={unitAfter}
            />
        </div>
    );
}

export const NumberFieldFilter = React.memo(
    NumberFieldFilterBase,
    (prevProps, nextProps) =>
        prevProps.fg === nextProps.fg &&
        prevProps.onFilterGroupsChange === nextProps.onFilterGroupsChange &&
        prevProps.filter === nextProps.filter &&
        prevProps.max === nextProps.max
);
